<script>
import {
  fetchBankerPromoCodes,
  createBankerPromoCodes,
  updateBankerPromoCodes,
  deleteBankerPromoCodes
} from '@domains/banker-promo-code/api'
import formModalBankerPromoCode from '@domains/banker-promo-code/components/modalBankerPromoCode/formBankerPromoCode.vue'
import infoModal from '@shared/components/shared/infoModal.vue'
import appModal from '@shared/components/shared/modal.vue'
import appTableSection from '@shared/components/shared/tableSection.vue'
import { useNoScroll } from '@shared/composables/useNoScroll'
import { handleGlobalError } from '@shared/services/errorHandler'
import { useHead } from '@unhead/vue'
import { onMounted, ref } from 'vue'
import { toast } from 'vue3-toastify'

export default {
  name: 'appBankerPromoCode',

  components: {
    appTableSection,
    infoModal,
    appModal,
    formModalBankerPromoCode
  },

  setup() {
    useHead({ title: 'Códigos de assessores parceiros' })

    const modalFormIsShown = ref(false)
    const modalConfig = ref({})

    const promoCodes = ref([])
    const loading = ref(false)

    const { setNoScroll } = useNoScroll()

    const tableHeaders = [
      { key: 'code', label: 'Código', type: 'text' },
      { key: 'institution', label: 'Instituição', type: 'text' },
      { key: 'clientCount', label: 'Qtde', type: 'info' },
      { key: 'description', label: 'Descrição', type: 'text' },
      {
        key: 'actions',
        label: 'Ações',
        type: 'actions',
        options: [
          { action: 'edit', label: 'Editar', class: 'edit', emitEvent: 'edit' },
          { action: 'delete', label: 'Deletar', class: 'delete', emitEvent: 'delete' }
        ]
      }
    ]

    const fetchBankerPromoCode = async () => {
      loading.value = true
      try {
        const response = await fetchBankerPromoCodes()
        promoCodes.value = response.data.payload
      } catch (e) {
        handleGlobalError(e)
      } finally {
        loading.value = false
      }
    }

    const resetPromoCodes = () => {
      promoCodes.value = []
    }

    onMounted(() => {
      fetchBankerPromoCode()
    })

    const title = ref('')
    const initialFormData = ref({})
    const modalEnabled = ref(false)
    const modalInfoEnabled = ref(false)
    const editModeEnabled = ref(false)
    const loadingButton = ref(false)

    const handleEdit = (item) => {
      title.value = `Editar código ${item.code}`
      initialFormData.value = { ...item }

      editModeEnabled.value = true
      modalEnabled.value = true
    }

    const handleInfo = (item) => {
      title.value = `Info`
      initialFormData.value = { ...item }
      modalInfoEnabled.value = true
    }

    const newPromoCode = () => {
      title.value = 'Adicionar código'
      modalEnabled.value = true
    }

    const handleFormCreation = async (formData) => {
      if (!editModeEnabled.value) {
        handleSubmitPromoCode(formData)
      } else {
        handleUpdatePromoCode(formData)
      }
    }

    const handleSubmitPromoCode = async (formData) => {
      loadingButton.value = true
      try {
        await createBankerPromoCodes(formData)
        modalEnabled.value = false
        resetPromoCodes()
        fetchBankerPromoCode()
        toast('Códigos de assessores parceiros cadastrado', {
          theme: 'auto',
          type: 'success',
          dangerouslyHTMLString: true
        })
      } catch (e) {
        handleGlobalError(e)
      } finally {
        initialFormData.value = {}
        loadingButton.value = false
        setNoScroll(false)
      }
    }

    const handleUpdatePromoCode = async (updatedForm) => {
      loadingButton.value = true
      try {
        await updateBankerPromoCodes(updatedForm)
        resetPromoCodes()
        fetchBankerPromoCode()
        modalEnabled.value = false
      } catch (e) {
        handleGlobalError(e)
      } finally {
        initialFormData.value = {}
        loadingButton.value = false
        editModeEnabled.value = false
        setNoScroll(false)
      }
    }

    const handleDelete = async (item) => {
      modalConfig.value = {
        origin: 'delete-news',
        title: 'Você tem certeza?',
        icon: 'bi bi-exclamation-square-fill',
        description:
          'Deseja mesmo apagar este código do assessores, ação não poderá ser revertida.',
        showButtons: true,
        type: 'warning',
        submitButtonText: 'Confirmar',
        backButtonText: 'Cancelar',
        id: item.id
      }
      modalFormIsShown.value = true
    }

    const handleConfirm = (item) => {
      if (item.origin === 'delete-news') {
        handleDeletePromoCode(item)
      }
    }

    const handleDeletePromoCode = async (item) => {
      try {
        loadingModal.value = true
        await deleteBankerPromoCodes(item.id)
        promoCodes.value = promoCodes.value.filter((op) => op.id !== item.id)
      } catch (e) {
        handleGlobalError(e)
      } finally {
        modalFormIsShown.value = false
        loadingModal.value = false
        setNoScroll(false)
      }
    }

    const loadingModal = ref(false)

    const handleFormClose = () => {
      initialFormData.value = {}
      editModeEnabled.value = false
      modalInfoEnabled.value = false
      modalEnabled.value = false
    }

    return {
      tableHeaders,
      promoCodes,
      loading,

      newPromoCode,

      modalFormIsShown,
      modalEnabled,
      modalInfoEnabled,
      modalConfig,
      title,

      handleFormClose,
      initialFormData,
      loadingButton,

      handleFormCreation,
      handleInfo,
      handleEdit,
      handleDelete,

      handleConfirm,
      loadingModal
    }
  }
}
</script>

<template>
  <div>
    <form-modal-banker-promo-code
      v-if="modalEnabled"
      :title="title"
      @onClose="handleFormClose"
      :loading="loadingButton"
      :initialFormData="initialFormData"
      @onSubmitted="handleFormCreation"
    />

    <info-modal
      v-if="modalInfoEnabled"
      class="col-12 form-table"
      @onClose="handleFormClose"
      :title="title"
    >
      <table>
        <thead>
          <tr>
            <th>ID</th>
            <th>Nome</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="client in initialFormData.clients" :key="client.clientId">
            <td>{{ client.clientId }}</td>
            <td>{{ client.clientName }}</td>
          </tr>
        </tbody>
      </table>
    </info-modal>

    <app-modal
      v-if="modalFormIsShown"
      :modalConfig="modalConfig"
      :loading="loadingModal"
      @submit="handleConfirm"
      @onClose="modalFormIsShown = false"
    />

    <div class="row title">
      <h2 class="col">Códigos de assessores parceiros</h2>
      <div class="col-12 col-md-3 form-input">
        <button class="secondary" @click="newPromoCode()">
          <i class="bi bi-node-plus-fill"></i>
          Novo Código de assessor
        </button>
      </div>
    </div>

    <app-table-section
      :items="promoCodes"
      :headers="tableHeaders"
      :loading="loading"
      :eventHandlers="{
        info: handleInfo,
        edit: handleEdit,
        delete: handleDelete
      }"
    />
  </div>
</template>
